/**
 * External dependencies
 */
import { useCallback, useMemo } from '@web-stories-wp/react';
import { useUnits } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import { useStory } from '../../app';
import getTransformableElements from './multiSelectionMoveable/getTransformableElements';
import { useTransform, useTransformHandler } from '../transform';
import {
  getTextBoxAttachmentTransformId,
  isTextBoxAttachment,
  resolveTextBoxAttachment,
} from '../../utils/textBoxAttachment';

function TextBoxAttachmentTransform({ element }) {
  const attachment = isTextBoxAttachment(element.textBoxAttachment)
    ? element.textBoxAttachment
    : null;
  const { currentPage, selectedElementIds } = useStory(({ state }) => ({
    currentPage: state.currentPage,
    selectedElementIds: state.selectedElementIds,
  }));
  const target = useMemo(
    () =>
      attachment
        ? currentPage?.elements.find(({ id }) => id === attachment.targetId)
        : null,
    [attachment, currentPage?.elements]
  );
  const isDirectlyTransformed = useMemo(() => {
    if (!selectedElementIds?.includes(element.id)) {
      return false;
    }
    if (selectedElementIds.length === 1) {
      return true;
    }
    const selectedIds = new Set(selectedElementIds);
    return getTransformableElements(
      (currentPage?.elements || []).filter(({ id }) => selectedIds.has(id))
    ).some(({ id }) => id === element.id);
  }, [currentPage?.elements, element.id, selectedElementIds]);
  const { getBox, dataToEditorX, dataToEditorY } = useUnits(({ actions }) => ({
    getBox: actions.getBox,
    dataToEditorX: actions.dataToEditorX,
    dataToEditorY: actions.dataToEditorY,
  }));
  const pushTransform = useTransform(({ actions }) => actions.pushTransform);

  const handleTargetTransform = useCallback(
    (transform) => {
      if (!pushTransform || !attachment || !target) {
        return;
      }
      if (transform === null) {
        pushTransform(element.id, null);
        return;
      }
      if (transform.attachmentResolved) return;
      if (isDirectlyTransformed && !transform.staticTransformation) {
        return;
      }
      if (
        !transform.translate &&
        typeof transform.rotate === 'undefined' &&
        !transform.resize
      ) {
        return;
      }

      const dependentBox = getBox(element);
      const targetBox = getBox(target);
      const [translateX = 0, translateY = 0] = transform.translate || [];
      const [resizeWidth, resizeHeight] = transform.resize || [];
      const transformedTarget = {
        ...targetBox,
        id: target.id,
        x: targetBox.x + translateX,
        y: targetBox.y + translateY,
        width: resizeWidth || targetBox.width,
        height: resizeHeight || targetBox.height,
        rotationAngle:
          typeof transform.rotate === 'number'
            ? transform.rotate
            : targetBox.rotationAngle,
      };
      const transformedDependent = resolveTextBoxAttachment(
        {
          ...dependentBox,
          id: element.id,
          type: element.type,
          textBoxAttachment: {
            ...attachment,
            gap: dataToEditorY(attachment.gap),
            crossAxisOffset: dataToEditorX(attachment.crossAxisOffset),
          },
        },
        transformedTarget
      );

      pushTransform(element.id, {
        staticTransformation: transform.staticTransformation === true,
        translate: [
          transformedDependent.x - dependentBox.x,
          transformedDependent.y - dependentBox.y,
        ],
        rotate: dependentBox.rotationAngle,
        resize: [dependentBox.width, dependentBox.height],
      });
    },
    [
      attachment,
      dataToEditorX,
      dataToEditorY,
      element,
      getBox,
      isDirectlyTransformed,
      pushTransform,
      target,
    ]
  );

  useTransformHandler(target?.id, handleTargetTransform, [
    target?.id,
    handleTargetTransform,
  ]);
  useTransformHandler(
    target?.id ? getTextBoxAttachmentTransformId(target.id) : null,
    handleTargetTransform,
    [target?.id, handleTargetTransform]
  );

  return null;
}

export default TextBoxAttachmentTransform;
